import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { refreshStoredUser, logout as doLogout, type SessionUser } from './auth';
import { getUser } from './session';

interface AuthContextValue {
  user: SessionUser | null;
  /** Sunucudan tazeleme sürüyor. İlk render'da yerel kopya zaten gösterilir. */
  verifying: boolean;
  /** Giriş/2FA sonrası bağlamı tazeler. */
  adopt: (user: SessionUser) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Oturum bağlamı.
 *
 * Desen: localStorage kopyasıyla ANINDA render et, mount'ta `/auth/me` ile
 * SUNUCUDAN tazele. Centramir'de bu daha da kritik — çünkü login yanıtı
 * `areas` taşımaz; menüyü doğru çizen tek kaynak `/auth/me`'dir.
 *
 * Yerel kopyaya güvenilerek hiçbir kapı açılmaz: `areas` yalnızca menüyü ve
 * yönlendirmeyi belirler, asıl kapı her istekte sunucudaki guard'dır.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<SessionUser | null>(() => getUser());
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const fresh = await refreshStoredUser();
      if (cancelled) return;
      setUserState(fresh);
      setVerifying(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const adopt = useCallback((next: SessionUser) => {
    setUserState(next);
    setVerifying(false);
  }, []);

  const signOut = useCallback(async () => {
    await doLogout();
    setUserState(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, verifying, adopt, signOut }),
    [user, verifying, adopt, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth, AuthProvider dışında kullanıldı');
  return ctx;
}
